const CASCADE_PEAK_PIXELS = 2.2;

export interface ICascadePlotPoint {
  x: number;
  y: number;
}

export interface ICascadeAmplitudeScale {
  floor: number;
  peak: number;
}

export interface ICascadeProjectorOptions {
  display3D: boolean;
  width: number;
  height: number;
  count: number;
  xMin: number;
  xMax: number;
  amplitudeFloor: number;
  amplitudePeak: number;
  paddingRight: number;
  depths?: readonly number[];
}

export interface ICascadeProjector {
  project: (frequency: number, amplitude: number, index: number) => ICascadePlotPoint;
  projectDepth: (frequency: number, amplitude: number, depth: number) => ICascadePlotPoint;
  frequencyAt: (screenX: number) => number | null;
  baseline: (index: number) => ICascadePlotPoint;
  plotLeft: number;
  plotRight: number;
  plotTop: number;
  plotBottom: number;
}

export const measureCascadeAmplitude = (
  spectra: readonly { items: readonly { y: number }[] }[],
): ICascadeAmplitudeScale => {
  let floor = Number.POSITIVE_INFINITY;
  let peak = Number.NEGATIVE_INFINITY;
  for (const spectrum of spectra) {
    for (const item of spectrum.items) {
      if (item.y < floor) floor = item.y;
      if (item.y > peak) peak = item.y;
    }
  }
  if (!Number.isFinite(floor) || !Number.isFinite(peak)) return { floor: 0, peak: 1 };
  return { floor, peak: Math.max(peak, floor + 1e-9) };
};

export const measureCascadeFrequency = (
  spectra: readonly { items: readonly { x: number }[] }[],
): { min: number; max: number } => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const spectrum of spectra) {
    for (const item of spectrum.items) {
      if (item.x < min) min = item.x;
      if (item.x > max) max = item.x;
    }
  }
  return Number.isFinite(min) ? { min, max: Math.max(max, min + 1e-9) } : { min: 0, max: 1 };
};

export const decimateCascadeTrace = (items: readonly ICascadePlotPoint[], maxPoints: number): ICascadePlotPoint[] => {
  if (items.length <= maxPoints) return items.map(item => ({ x: item.x, y: item.y }));
  const buckets = Math.max(1, Math.floor(maxPoints / 2));
  const size = items.length / buckets;
  const result: ICascadePlotPoint[] = [];
  for (let bucket = 0; bucket < buckets; bucket += 1) {
    const start = Math.floor(bucket * size);
    const end = Math.max(start + 1, Math.min(items.length, Math.floor((bucket + 1) * size)));
    let low = items[start];
    let high = items[start];
    for (let index = start; index < end; index += 1) {
      if (items[index].y < low.y) low = items[index];
      if (items[index].y >= high.y) high = items[index];
    }
    const pair = low.x <= high.x ? [low, high] : [high, low];
    for (const point of pair) {
      const last = result[result.length - 1];
      if (!last || last.x !== point.x || last.y !== point.y) result.push({ x: point.x, y: point.y });
    }
  }
  return result;
};

const TIME_STEPS_MS = [1000, 5000, 15000, 30000, 60000, 300000, 900000, 1800000, 3600000, 7200000, 21600000, 86400000];

export const normalizeCascadeDepths = (values: readonly (number | null)[]): number[] => {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;
  for (const value of values) {
    if (value === null || !Number.isFinite(value)) continue;
    if (value < min) min = value;
    if (value > max) max = value;
  }
  if (!Number.isFinite(min) || !(max > min)) return values.map(() => 0);
  const span = max - min;
  return values.map(value => (value === null || !Number.isFinite(value) ? 0 : (value - min) / span));
};

export const niceTimeTicks = (min: number, max: number, count = 6): number[] => {
  if (!(max > min)) return [min];
  const rough = (max - min) / count;
  const step = TIME_STEPS_MS.find(item => item >= rough) ?? TIME_STEPS_MS[TIME_STEPS_MS.length - 1];
  const start = Math.ceil((min - step * 1e-6) / step) * step;
  const ticks: number[] = [];
  for (let value = start; value <= max + step * 1e-6; value += step) ticks.push(value);
  return ticks.length ? ticks : [min, max];
};

export const spacedScreenLabels = <T extends ICascadePlotPoint>(points: readonly T[], gap: number): T[] => {
  if (points.length <= 1) return [...points];
  const kept: T[] = [points[0]];
  for (let index = 1; index < points.length - 1; index += 1) {
    const previous = kept[kept.length - 1];
    const point = points[index];
    if (Math.hypot(point.x - previous.x, point.y - previous.y) >= gap) kept.push(point);
  }
  const end = points[points.length - 1];
  while (kept.length > 1 && Math.hypot(end.x - kept[kept.length - 1].x, end.y - kept[kept.length - 1].y) < gap)
    kept.pop();
  kept.push(end);
  return kept;
};

export const niceCascadeTicks = (min: number, max: number, count = 6): number[] => {
  if (!(max > min)) return [min];
  const rough = (max - min) / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const residual = rough / magnitude;
  const step = (residual >= 7.5 ? 10 : residual >= 3.5 ? 5 : residual >= 1.5 ? 2 : 1) * magnitude;
  const start = Math.ceil((min - step * 1e-6) / step) * step;
  const ticks: number[] = [];
  for (let value = start; value <= max + step * 1e-6; value += step) ticks.push(Number(value.toPrecision(12)));
  return ticks.length ? ticks : [min, max];
};

export const cascadeScaleTicks = (min: number, max: number, count = 6): number[] => {
  const span = Math.max(max - min, 1e-9);
  const ticks = niceCascadeTicks(min, max, count).filter(
    tick => tick >= min - span * 1e-6 && tick <= max + span * 1e-6,
  );
  const withEnds = [...ticks];
  if (!withEnds.some(tick => Math.abs(tick - min) <= span * 0.02)) withEnds.unshift(min);
  if (!withEnds.some(tick => Math.abs(tick - max) <= span * 0.02)) withEnds.push(max);
  return withEnds;
};

export const createCascadeProjector = ({
  display3D,
  width,
  height,
  count,
  xMin,
  xMax,
  amplitudeFloor,
  amplitudePeak,
  paddingRight,
  depths,
}: ICascadeProjectorOptions): ICascadeProjector => {
  const plotLeft = 78;
  const plotRight = Math.max(plotLeft + 1, width - paddingRight);
  const plotTop = 16;
  const plotBottom = Math.max(plotTop + 1, height - 36);
  const xSpan = Math.max(xMax - xMin, 1e-9);
  const amplitudeSpan = Math.max(amplitudePeak - amplitudeFloor, 1e-9);
  const slots = Math.max(count - 1, 1) + 2.6;
  const slot = (plotBottom - plotTop) / slots;
  const plotWidth = plotRight - plotLeft;
  const plotHeight = plotBottom - plotTop;
  const figureScale = 1.32;
  const freqWidth = plotWidth * 0.58 * figureScale;
  const depthX = plotWidth * 0.36 * figureScale;
  const depthY = plotHeight * 0.4 * figureScale;
  const peakPixels = plotHeight * 0.45 * figureScale;

  const depthOf = (index: number): number => {
    const value = depths?.[index];
    if (value !== undefined && Number.isFinite(value)) return Math.min(1, Math.max(0, value));
    return count <= 1 ? 0 : index / Math.max(count - 1, 1);
  };
  const travel = Math.max(count - 1, 1) * slot;
  const projectDepth = (frequency: number, amplitude: number, depth: number): ICascadePlotPoint => {
    const freqNorm = (frequency - xMin) / xSpan;
    const ampNorm = (amplitude - amplitudeFloor) / amplitudeSpan;
    const clamped = Math.min(1, Math.max(0, depth));
    if (!display3D) {
      const baseline = plotBottom - 8 - clamped * travel;
      return {
        x: plotLeft + freqNorm * (plotRight - plotLeft),
        y: baseline - ampNorm * slot * CASCADE_PEAK_PIXELS,
      };
    }
    const shrink = 1 - clamped * 0.35;
    return {
      x: plotLeft + clamped * depthX + freqNorm * freqWidth * shrink,
      y: plotBottom - clamped * depthY - ampNorm * peakPixels * shrink,
    };
  };
  const project = (frequency: number, amplitude: number, index: number): ICascadePlotPoint =>
    projectDepth(frequency, amplitude, depthOf(index));

  const frequencyAt = (screenX: number): number | null => {
    const axisLeft = plotLeft;
    const axisWidth = display3D ? freqWidth : plotRight - plotLeft;
    if (!(axisWidth > 0) || screenX < axisLeft || screenX > axisLeft + axisWidth) return null;
    return xMin + ((screenX - axisLeft) / axisWidth) * xSpan;
  };

  return {
    project,
    projectDepth,
    frequencyAt,
    baseline: index => project(xMin, amplitudeFloor, index),
    plotLeft,
    plotRight,
    plotTop,
    plotBottom,
  };
};

export interface ICascadeHit {
  index: number;
  amplitude: number;
  frequency: number;
  x: number;
  y: number;
}

export const nearestCascadeSample = (
  traces: readonly { index: number; points: readonly ICascadePlotPoint[] }[],
  project: ICascadeProjector["project"],
  screenX: number,
  screenY: number,
  maxDistance = 16,
): ICascadeHit | null => {
  let best: ICascadeHit | null = null;
  let bestDistance = maxDistance;
  for (const trace of traces) {
    for (const point of trace.points) {
      const screen = project(point.x, point.y, trace.index);
      const distance = Math.hypot(screen.x - screenX, screen.y - screenY);
      if (distance > maxDistance) continue;
      const closer = distance < bestDistance - 0.5;
      const frontTie = Math.abs(distance - bestDistance) <= 0.5 && (best === null || trace.index < best.index);
      if (best === null || closer || frontTie) {
        bestDistance = distance;
        best = { index: trace.index, amplitude: point.y, frequency: point.x, x: screen.x, y: screen.y };
      }
    }
  }
  return best;
};
